import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Play, Sparkles, Share2, Film } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const HERO_BG =
  "https://images.unsplash.com/photo-1696332415657-9fbe802a8875?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjB5b3V0aCUyMGZpbG0lMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3ODE1NjA0NDV8MA&ixlib=rb-4.1.0&q=85";

const POSTERS = [
  {
    title: "SUMMER 2026",
    tag: "the one with the sparklers",
    url: "https://images.unsplash.com/photo-1758523981337-7508dda01c3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxmcmllbmRzJTIwc3VtbWVyJTIwbmlnaHQlMjBwb2xhcm9pZHxlbnwwfHx8fDE3ODE1NjA0NDV8MA&ixlib=rb-4.1.0&q=85",
    grad: "from-amber-700/40 to-rose-900/80",
    vibe: "Nostalgic",
  },
  {
    title: "MY SENIOR YEAR",
    tag: "you only get one",
    url: "https://images.unsplash.com/photo-1721420586808-19cadbc497e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxmcmllbmRzJTIwc3VtbWVyJTIwbmlnaHQlMjBwb2xhcm9pZHxlbnwwfHx8fDE3ODE1NjA0NDV8MA&ixlib=rb-4.1.0&q=85",
    grad: "from-purple-700/40 to-black/90",
    vibe: "Cinematic",
  },
  {
    title: "FUTURE ME",
    tag: "where I'll be in ten years",
    url: "https://images.unsplash.com/photo-1496559249665-c7e2874707ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjB5b3V0aCUyMGZpbG0lMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3ODE1NjA0NDV8MA&ixlib=rb-4.1.0&q=85",
    grad: "from-red-700/40 to-slate-900/90",
    vibe: "Hype",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard" : "/login";

  return (
    <div className="min-h-screen bg-[#050505] text-white relative" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden pt-20">
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 pb-24 w-full">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-[#FF2A2A]" />
            <span data-testid="hero-eyebrow" className="font-mono text-xs uppercase tracking-[0.4em] text-[#FF2A2A]">
              A LIFEREPLAY ORIGINAL
            </span>
          </div>

          <h1 className="font-display font-black text-6xl sm:text-7xl lg:text-[8.5rem] leading-[0.85] tracking-tighter max-w-5xl">
            YOUR LIFE
            <br />
            DESERVES
            <br />
            <span className="text-[#FF2A2A]">A TRAILER.</span>
          </h1>

          <p className="font-emotional text-2xl sm:text-3xl text-white/70 mt-8 max-w-2xl leading-snug">
            Drop your camera roll. AI scores it like A24. Share it like Netflix.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-12 stagger">
            <Link
              to={ctaTo}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF5555] text-white font-mono uppercase tracking-widest text-sm px-8 py-4 transition-colors"
            >
              <Play className="w-4 h-4 fill-white" />
              Create my trailer
            </Link>
            <Link
              to={ctaTo}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-white/60 text-white/80 hover:text-white font-mono uppercase tracking-widest text-sm px-8 py-4 transition-all backdrop-blur-md bg-white/5"
            >
              See examples
            </Link>
          </div>

          <div className="absolute bottom-6 right-6 sm:right-12 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hidden sm:block">
            EST. 2026 · NOW SHOWING
          </div>
        </div>
      </section>

      {/* POSTERS / FEATURED */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-3">
              Now showing
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
              Lives, in their best edits.
            </h2>
          </div>
          <div className="font-emotional text-lg text-white/50">
            Pick a vibe. Upload memories. Press play.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {POSTERS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`featured-poster-${i}`}
              className="group relative aspect-[2/3] overflow-hidden rounded-sm poster-shadow"
            >
              <img
                src={p.url}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${p.grad} mix-blend-multiply opacity-70`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] bg-white/10 backdrop-blur px-2 py-1 text-white/80">
                  {p.vibe}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-display font-black text-3xl tracking-tight leading-none">
                  {p.title}
                </h3>
                <p className="font-emotional text-base text-white/70 mt-2">{`"${p.tag}"`}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32 border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {[
            { icon: Film, n: "01", t: "Drop your life in", d: "Photos, voice notes, screenshots, your weird summer pics. Anything goes." },
            { icon: Sparkles, n: "02", t: "AI scores the cut", d: "Claude writes the narration. OpenAI lays down a cinematic voiceover. You watch your year like a Netflix trailer." },
            { icon: Share2, n: "03", t: "Send it to the group chat", d: "Share a public trailer link. Watch your friends panic-make theirs." },
          ].map(({ icon: Icon, n, t, d }) => (
            <div key={n} data-testid={`how-step-${n}`} className="border-l border-white/10 pl-6">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF2A2A] mb-4">
                STEP {n}
              </div>
              <Icon className="w-8 h-8 mb-6 text-white/40" />
              <h3 className="font-display font-bold text-2xl tracking-tight mb-3">{t}</h3>
              <p className="text-white/60 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative max-w-5xl mx-auto px-6 sm:px-12 py-24 sm:py-40 text-center">
        <h2 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
          The story
          <br />
          <span className="font-emotional font-normal text-white/60">they&apos;ll tell about you </span>
          <br />
          starts here.
        </h2>
        <Link
          to={ctaTo}
          data-testid="footer-cta"
          className="inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF5555] text-white font-mono uppercase tracking-widest text-sm px-10 py-5 mt-12 transition-colors"
        >
          <Play className="w-4 h-4 fill-white" />
          Start my first episode
        </Link>
        <div className="mt-16 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
          © 2026 LifeReplay · A LifeReplay Original
        </div>
      </section>
    </div>
  );
}
